import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import { Navigate } from "react-router-dom";
import Swal from "sweetalert2";
import InformacionUsuario from "../modales/InformacionUsuario";
import CenteredAvatar from "../modales/AvatarCentrado";

import {API_BASE} from "../../services/apirest";
import { invalidateSolicitudesCache } from "../../services/solicitudesCache";

const bajaStageLabels = {
  SolicitudBaja: "Pendiente validación de baja",
  CapturaBaja: "Captura de baja",
  Ejecutada: "Baja ejecutada",
  VistoDistribuidor: "Visto",
};

const reactivacionStageLabels = {
  SolicitudReactivacion: "Enviado para reactivación",
  Ejecutado: "Reactivación lista",
  VistoDistribuidor: "Visto",
};


export default function CompletedSolicitudes() {
  const rolId = parseInt(localStorage.getItem("rol_id"), 10);
  const [profile, setProfile] = useState(null);
  const [filterEstado] = useState("Todos");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchPhone, setSearchPhone] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const roleLabels = {
  8:  "VENDEDOR (ACTIVE DIRECTORY/SIEBEL TELCOM)",
  9:  "VENDEDOR / INSTALADOR (TOA)",
  10: "SUPERVISOR",
  11: "ADMINISTRATIVO",
};
const getRolNombre = (id) => roleLabels?.[Number(id)] ?? `Rol ${id}`;
  const headers = useMemo(
      () => ({
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      }),
      []
  );

    const ultimosRoles = [
    { id: 8, nombre:  "VENDEDOR (ACTIVE DIRECTORY/SIEBEL TELCOM)" },
    { id: 9, nombre:  "VENDEDOR / INSTALADOR (TOA)" },
    { id: 10, nombre: "SUPERVISOR" },
    { id: 11, nombre: "ADMINISTRATIVO" },
  ];

const isBajaActive = (s) =>
  !!s?.baja_stage && s.baja_stage !== "VistoDistribuidor";

// arriba, junto a tus otros helpers:
  const isReactivacionActive = (s) =>
      !!s?.reactivacion_stage && s.reactivacion_stage !== "VistoDistribuidor";

const isModActive = (s) =>
  !!s?.modificaciones_stage && s.modificaciones_stage !== "VistoDistribuidorMod";

const refreshBadges = () => {
  invalidateSolicitudesCache(key => key.includes("/solicitudes.php"));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("solicitudes:refresh"));
    window.dispatchEvent(new Event("inbox:refresh"));
  }
};

  // filtro de lista visible
  const filteredList = useMemo(() => {
    if (!Array.isArray(list)) return [];
    return list
        .filter((s) => !searchPhone || s.telefono?.includes(searchPhone))
        .filter((s) => {
          const target = (s.usuario_red ?? "").toLowerCase();
          return !searchEmail || target.includes(searchEmail.toLowerCase());
        });
  }, [list, searchPhone, searchEmail]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort(
        (a, b) => new Date(b.Fecha) - new Date(a.Fecha)
    );
  }, [filteredList]);

  // Carga perfil
  useEffect(() => {
    fetch(`${API_BASE}/perfil.php`, { headers })
        .then((r) => r.json())
        .then((d) => d.status === "success" && setProfile(d.result.user))
        .catch(console.error);
  }, [headers]);

  // Carga solicitudes según filterEstado
useEffect(() => {
  if ((rolId === 3 || rolId === 4) && !profile) return;

  const estados = [
    "Pendiente",
    "En proceso",
    "Registrando",
    "Completado",
    "Visto",
    "Rechazado",
    "Progreso",
  ];

  const load = async () => {
    try {
      let items = [];

      if (filterEstado === "Todos") {
        const respuestas = await Promise.all(
          estados.map((e) =>
            fetch(`${API_BASE}/solicitudes.php?Estado=${encodeURIComponent(e)}`, { headers })
              .then((r) => r.json())
              .then((d) => d.result.solicitudes || [])
          )
        );
        items = respuestas.flat();
      } else {
        const res = await fetch(
          `${API_BASE}/solicitudes.php?Estado=${encodeURIComponent(filterEstado)}`,
          { headers }
        );
        if (!res.ok) throw new Error(res.statusText);
        items = (await res.json()).result.solicitudes || [];
      }

      // ── FILTRADO UNIFICADO ──
     items = items.filter((s) => {
      if (isBajaActive(s)) return true;
      if (isModActive(s)) return true;
      return s.Estado !== "Visto";
    });

      // Filtrado por rol
      if (rolId === 3) {
        const first = (...cands) => cands.find(v => v !== undefined && v !== null && v !== "");
        const norm = (v) => (v ? String(v).trim().toLowerCase() : "");
        const mySup = norm(first(profile.correo_supervisor, profile.Usuario, profile.usuario, profile.email));
        if (!mySup) {
          items = [];
        } else {
          items = items.filter(s => {
            const sup = norm(first(s.correo_supervisor, s.supervisor_correo, s.supervisor_email));
            return sup === mySup;
          });
        }
      } else if (rolId === 4) {
        items = items.filter((s) => Number(s.distribuidor_id) === Number(profile.distribuidor_id));
      }

      setList(items);
    } catch (err) {
      console.error(err);
      setList([]);
    }
  };

  load();
}, [headers, rolId, profile, filterEstado]);

  // Guardia de ruta
  if (![1, 3, 4, 14].includes(rolId)) return <Navigate to="/" replace />;

  // Spinner mientras carga
  if (list === null) {
    return (
        <Container
            className="d-flex justify-content-center"
            style={{ height: "60vh" }}
        >
          <Spinner animation="border" />
        </Container>
    );
  }

  const isPlaceholder = (v) => {
    if (v == null) return true;
    const s = String(v).trim();
    if (!s) return true;
    const low = s.toLowerCase();
    if (["null", "undefined", "n/a", "na"].includes(low)) return true;
    if (/^[-\u2013\u2014]+$/.test(s)) return true;
    return false;
  };

  const isOn = (f) => f === 1 || f === "1" || f === true;

  const markCred = (text, flag) => (isOn(flag) || !isPlaceholder(text) ? "✅" : "❌");

  const handleConfirmVistoMod = async (SolicitudId) => {
  try {
    const res = await fetch(`${API_BASE}/solicitudes.php`, {
      method: "PUT",
      headers,
      body: JSON.stringify({
        action: "confirmar_modificacion",
        SolicitudId,
      }),
    });
    const d = await res.json();
    if (d.status === "success" || d.status === "ok") {
      setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
      Swal.fire("Listo", d.message || "Modificación confirmada (Visto).", "success");
      refreshBadges();
    } else {
      Swal.fire("Error", d.result?.error_msg || "No se pudo confirmar", "error");
    }
  } catch (err) {
    Swal.fire("Error", "Fallo en la petición", "error");
  }
};

 const renderEstadoLabel = (sol) => {
  const { Estado, fgu_usuario, red_siebel_telcom, fgu_toa } = sol;
  switch (Estado) {
    case "Pendiente":
      return "Pendiente Validación";
    case "En proceso":
      return "Pendiente revisión";
    case "Aprobado":
      return "Pendiente revisión";
    case "Registrando": {
      const necesitaTOA   = rolId === 9;            // sólo rol 9 necesita TOA
      const faltaUsuario = !fgu_usuario;
      const faltaRed     = !red_siebel_telcom;
      const faltaTOA     = necesitaTOA && !fgu_toa; // sólo chequea TOA si aplica
      if (faltaUsuario || faltaRed || faltaTOA) {
        return "Creación de FGU";
      }
      return "Generar Credenciales";
    }
    case "Completado":
      return "Pendiente de recepción";
    default:
      return Estado;
  }
};
  /*Devuelve quién es responsable en cada etapa, siguiendo el flujo: Distribuidor(4) → Supervisor(3) → Capturista(1) → Distribuidor(4).*/
function getResponsable(s) {
  // BAJA primero
  if (isBajaActive(s)) {
    switch (s.baja_stage) {
      case "SolicitudBaja": return s.supervisor || "(Supervisor no asignado)";
      case "CapturaBaja":   return "Analista";
      case "Rechazada":
      case "Ejecutada":
        return (`${s.creado_por_nombre || ""} ${s.creado_por_apellido || ""}`).trim() || "(Distribuidor)";
      default: return "—";
    }
  }

  // Luego MOD
  if (isModActive(s)) {
    switch (s.modificaciones_stage) {
      case "SolicitudModificacion": return s.supervisor || "(Supervisor no asignado)";
      case "Aprobada":              return "Analista";
      case "Ejecutada":
      case "VistoDistribuidorMod":
        return (`${s.creado_por_nombre || ""} ${s.creado_por_apellido || ""}`).trim() || "(Distribuidor)";
      default: return "—";
    }
  }
    // REACTIVACIÓN
    if (isReactivacionActive(s)) {
      switch (s.reactivacion_stage) {
        case "SolicitudReactivacion": return "Analista";
        case "Ejecutada":           return s.distribuidor_nombre || "(Supervisor no asignado)";
        case "VistoDistribuidor":
          return (`${s.creado_por_nombre || ""} ${s.creado_por_apellido || ""}`).trim() || "(Distribuidor)";
        default: return "—";
      }
    }
  // Sin subflujos
  switch (s.Estado) {
    case "Pendiente":    return s.supervisor || "(Supervisor)";
    case "En proceso":   return "Validador";
    case "Registrando":  return "Analista";
    case "Completado":
    case "Visto":
      return (`${s.creado_por_nombre || ""} ${s.creado_por_apellido || ""}`).trim() || "(Distribuidor)";
    default: return "—";
  }
}

const getTipoSolicitud = (s) => {
  if (isBajaActive(s)) return "Solicitud de baja";
  if (isModActive(s))  return "Solicitud de modificación";
  if (isReactivacionActive(s)) return "Solicitud de reactivación";
  return "Solicitud de usuario";
};

const getEstadoDisplay = (s) => {
  if (isBajaActive(s)) {
    return bajaStageLabels[s.baja_stage] || s.baja_stage;
  }
  if (isModActive(s)) {
    return modStageLabels[s.modificaciones_stage] || s.modificaciones_stage;
  }
  if (isReactivacionActive(s)) {
    return reactivacionStageLabels[s.reactivacion_stage] || s.reactivacion_stage; // ← NUEVO
  }
  return renderEstadoLabel(s);
};

const modStageLabels = {
  SolicitudModificacion: "Pendiente modificación",
  Aprobada:              "Modificación aprobada",
  Ejecutada:             "Modificación ejecutada",
  Rechazada:             "Modificación rechazada",
  VistoDistribuidorMod:  "Modificación recibida",
};
  // Confirmar visto de baja ejecutada (rol 4)
  // marca como "Visto" normal (registro), sin tocar baja_stage
  const handleMarcarVistoRegistro = async (sol) => {
    try {
      const SolicitudId = sol?.SolicitudId;

      // Campos a mostrar (con fallbacks)
      const usuarioRed = sol?.usuario_red ?? "";
      const cvven      = sol?.cvven ?? sol?.ccven ?? "";
      const numeroEmpleado = sol?.numero_vendedor ??"";

      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ SolicitudId, Estado: "Visto" }),
      });
      const d = await res.json();

      if (d.status === "success" || d.status === "ok") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));

        // Helpers
        const esc = (v) =>
            String(v ?? "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
        const fmt = (v) => (isPlaceholder(v) ? "—" : esc(String(v)));

        Swal.fire({
          icon: "success",
          // Título pequeño
          title: '<span style="font-size:28px;font-weight:600;display:block">Listo</span>',
          // Contenido centrado y grande
          html: `
          <div style="text-align:center">
            <div style="font-size:20px;line-height:1.6">
               <div><b>Numero Empleado:</b> ${fmt(numeroEmpleado)}</div>
              <div><b>Usuario red:</b> ${fmt(usuarioRed)}</div>
              <div><b>CVVEN:</b> ${fmt(cvven)}</div>
            </div>
          </div>
        `,
          width: 600,
          confirmButtonText: "Aceptar",
        });
      } else {
        Swal.fire("Error", d.result?.error_msg || "No se pudo marcar como visto", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Fallo en la petición", "error");
    }
  };

// confirma visto de baja ejecutada: usa el action específico
  const handleConfirmVistoBaja = async (SolicitudId) => {
    try {
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          action: "confirmar_visto_baja",
          SolicitudId,
        }),
      });
      const d = await res.json();
      if (d.status === "success" || d.status === "ok") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
        Swal.fire("Listo", "Visto de baja confirmado.", "success");
        refreshBadges();
      } else {
        Swal.fire("Error", d.result?.error_msg || "No se pudo confirmar visto", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Fallo en la petición", "error");
    }
  };

  const handleConfirmVistoReactivacion = async (SolicitudId) => {
    try {
      // 1) Intento con la acción específica del backend
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          action: "confirmar_visto_reactivacion",
          SolicitudId,
        }),
      });
      const d = await res.json();

      if (d.status === "success" || d.status === "ok") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
        Swal.fire("Listo", "Reactivación marcada como vista.", "success");
        refreshBadges();
        return;
      }

      const res2 = await fetch(`${API_BASE}/solicitudes.php`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          SolicitudId,
          Estado: "Visto",
          reactivacion_stage: "VistoDistribuidor",
        }),
      });
      const d2 = await res2.json();

      if (d2.status === "success" || d2.status === "ok") {
        setList((prev) => prev.filter((s) => s.SolicitudId !== SolicitudId));
        Swal.fire("Listo", "Reactivación marcada como vista.", "success");
        refreshBadges();
      } else {
        Swal.fire("Error", d2.result?.error_msg || "No se pudo confirmar visto", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Fallo en la petición", "error");
    }
  };

  // Exportar CSV
  const exportCsv = () => {
    if (!list.length) return;
    const headersCsv = [
      "NOMBRE DE VENDEDOR",
      "TELEFONO",
      "CORREO",
      "FECHA ALTA",
      "RFC VENDEDOR",
      "NO. DE EMPLEADO",
      "TIPO DE SOLICITUD",
      "ESTADO",
      "SUPERVISOR DISTRIBUIDOR",
    ];
    const rows = list.map((s) => [
      `${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`.trim(),
      s.telefono || "",
      s.usuario || "",
      s.Fecha ? new Date(s.Fecha).toLocaleString() : "",
      s.rfc || "",
      s.numero_vendedor || "",
      getTipoSolicitud(s),
      getEstadoDisplay(s),
      s.supervisor_distribuidor || "",
    ]);
    const csv = [
      headersCsv.join(","),
      ...rows.map((r) =>
          r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `solicitudes_${filterEstado.toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="app-container">
      <Container className="my-5">
        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Solicitudes</h5>
            </div>
            <Badge bg={filteredList.length > 0 ? "info" : "secondary"} pill>
              {filteredList.length}
            </Badge>
          </Card.Header>
          <Card.Body>
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Form.Control
                placeholder="Buscar por teléfono..."
                style={{ maxWidth: 200 }}
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                className="me-2 mb-2"
              />
              <Form.Control
                placeholder="Buscar por RFC o usuario red..."
                style={{ maxWidth: 220 }}
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="me-2 mb-2"
              />
              <div className="ms-auto mb-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={exportCsv}
                  disabled={!list.length}
                >
                  Exportar
                </Button>
              </div>
            </div>

            {filteredList.length === 0 ? (
              <p className="text-center text-muted">No hay solicitudes.</p>
            ) : (
              <Table responsive hover striped className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Creado Por</th>
                    <th>Nombre</th>
                    <th>Telefono</th>
                    <th>Correo</th>
                    <th>Fecha</th>
                    <th>RFC</th>
                    <th>Num. Empleado</th>
                    <th>Tipo de solicitud</th>
                    <th>Estado</th>
                    <th>Responsable</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedList.map((s) => (
                    <tr
                      key={s.SolicitudId}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelected(s);
                        setShowDetailModal(true);
                      }}
                    >
                      <td>
                        {s.creado_por_nombre} {s.creado_por_apellido}
                      </td>
                      <td>
                        {s.nombre_usuario} {s.apellido_paterno}{" "}
                        {s.apellido_materno}
                      </td>
                      <td>{s.telefono}</td>
                      <td>{s.usuario}</td>
                      <td>{s.Fecha}</td>
                      <td>{s.rfc}</td>
                      <td>{s.numero_vendedor || "-"}</td>
                      <td>{getTipoSolicitud(s)}</td>
                      <td>{getEstadoDisplay(s)}</td>
                      <td>{getResponsable(s)}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {rolId === 4 && (
                            <>
                              {/* MOD: un solo bloque para Ejecutada/Rechazada */}
                              {!isBajaActive(s) && isModActive(s) && (
                                <>
                                  {s.modificaciones_stage === "Rechazada" && (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      className="ms-1"
                                      onClick={() => handleConfirmVistoMod(s.SolicitudId)}
                                    >
                                      Aceptar Rechazo
                                    </Button>
                                  )}

                                  {s.modificaciones_stage === "Ejecutada" && (
                                      <Button
                                      variant="warning"
                                      size="sm"
                                      className="ms-1"
                                      onClick={() => handleConfirmVistoMod(s.SolicitudId)}
                                    >
                                      Confirmar Modificación
                                    </Button>
                                  )}
                                </>
                              )}

                              {/* Registro sin subflujos activos */}
                              {!isBajaActive(s) && !isModActive(s) &&
                                  (s.Estado === "Completado" || s.Estado === "Rechazado") && (
                                      <Button
                                          variant="success"
                                          size="sm"
                                          onClick={() => handleMarcarVistoRegistro(s)}   // ← antes enviabas s.SolicitudId
                                      >
                                        Aceptar
                                      </Button>
                                  )}

                              {/* BAJA ejecutada */}
                              {s.baja_stage === "Ejecutada" && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleConfirmVistoBaja(s.SolicitudId)}
                                  className="ms-1"
                                >
                                  Confirmar Baja
                                </Button>
                              )}

                              {/* REACTIVACIÓN ejecutada */}
                              {s.reactivacion_stage === "Ejecutada" && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleConfirmVistoReactivacion(s.SolicitudId)}
                                  className="ms-1"
                                >
                                  Confirmar Reactivación
                                </Button>
                              )}
                            </>
                          )}
                        </td>

                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Modal detalle */}
        <Modal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          size="lg"
          centered
          style={{ zIndex: 11000 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Detalle de Solicitud</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selected && (
                <>
                {/* 1) foto */}
            <CenteredAvatar src={selected?.foto_url} alt="Foto del vendedor" />
                  {/* 1) Distribuidor */}
                  <InformacionUsuario
                      user={selected}
                      title="Distribuidor"
                      cols={2}
                      fields={[
                        { key: "distribuidor_nombre", label: "Distribuidor" },
                        { key: "distribuidor_rs",     label: "Razón social" },
                      ]}
                  />

                  {/* 2) Estado de la solicitud */}
                  <InformacionUsuario
                      user={selected}
                      title="Estado de la solicitud"
                      cols={2}
                      fields={[
                        { key: "tipo",   label: "Tipo de solicitud", render: (u) => getTipoSolicitud(u) },
                        { key: "estado", label: "Estado",            render: (u) => getEstadoDisplay(u) },
                      ]}
                  />

                  {/* 3) Datos del usuario */}
                  <InformacionUsuario
                      user={selected}
                      title="Datos del usuario"
                      cols={3}
                      fields={[
                        { key: "usuario", label: "Email" },
                        {
                          key: "nombre_completo",
                          label: "Nombre completo",
                          render: (u) =>
                              [u?.nombre_usuario, u?.apellido_paterno, u?.apellido_materno]
                                  .filter(Boolean)
                                  .join(" "),
                        },
                        { key: "telefono",         label: "Teléfono" },
                        {
                          key: "Fecha",
                          label: "Fecha",
                          render: (u) => (u?.Fecha ? new Date(u.Fecha).toLocaleDateString("es-MX") : "—"),
                        },
                        { key: "rfc",               label: "RFC", render: (u) => (u?.rfc || "").toUpperCase() },
                        { key: "numero_vendedor",   label: "Número Empleado" },
                      ]}
                  />

                   {/* Puesto actual */}
                                        <InformacionUsuario
                                            user={selected}
                                            title="Puesto actual"
                                            cols={2}
                                            fields={[
                                              {
                                                key: "rol_id",
                                                label: "Puesto",
                                                render: (u) => ultimosRoles.find((r) => r.id === u?.rol_id)?.nombre || "-",
                                              },
                                            ]}
                                        />

                  {/* 4) Credenciales (mostrar placeholders si faltan) */}
                  <InformacionUsuario
                      user={selected}
                      title="Credenciales"
                      cols={3}
                      hideEmpty={false}
                      fields={[
                        { key: "usuario_red",       label: "Usuario Red",    render: (u) => u?.usuario_red ?? "—" },
                        { key: "cvven", label: "CVVEN", render: (u) => u?.cvven ?? "—" },
                        // Si no es rol 9, ocultamos el campo devolviendo vacío:
                        { key: "fgu_toa", label: "Usuario TOA", render: (u) => markCred(u?.fgu_toa, u?.check_toa) },
                        { key: "fgu_pi",  label: "PI", render: (u) => markCred(u?.fgu_pi, u?.check_pi_siebel) },
                      ]}
                  />

                  {/* 5) Bodega (SOLO si hay datos) */}
                    {(
                      selected?.bodega_distribuidor ||
                      selected?.bodega_almacen ||
                      selected?.bodega_nombre ||
                      selected?.bodega_region ||
                      selected?.bodega_direccion
                    ) && (
                      <InformacionUsuario
                        user={selected}
                        title="Bodega"
                        cols={2}
                        hideEmpty={true}
                        fields={[
                          { key: "bodega_distribuidor", label: "Distribuidor" },
                          { key: "bodega_almacen",      label: "Almacén" },
                          { key: "bodega_nombre",       label: "Bodega" },
                          { key: "bodega_region",       label: "Región" },
                          { key: "bodega_direccion",    label: "Dirección" },
                        ]}
                      />
                    )}

                  {/* 5) Detalle de baja (condicional) */}
                  {(selected?.baja_stage === "Ejecutada" || selected?.baja_stage === "VistoDistribuidor") && (
                      <InformacionUsuario
                          user={selected}
                          title="Detalle de baja"
                          cols={3}
                          hideEmpty={false}
                          fields={[
                            { key: "folio_baja",  label: "Folio de baja", render: (u) => u?.folio_baja ?? "—" },
                            {
                              key: "fecha_baja",
                              label: "Fecha de baja",
                              render: (u) => (u?.fecha_baja ? new Date(u.fecha_baja).toLocaleString("es-MX") : "—"),
                            },
                            { key: "razon_baja",  label: "Razón de baja", render: (u) => u?.razon_baja ?? "—" },
                          ]}
                      />
                  )}

                  {/* 6) Detalle de modificación (condicional) */}
                  {(selected?.modificaciones_stage === "Ejecutada" ||
                      selected?.modificaciones_stage === "VistoDistribuidorMod" ||
                      selected?.modificaciones_stage === "Rechazada") && (() => {
                    let modData = {};
                    try { modData = JSON.parse(selected?.modificaciones_data || "{}"); } catch {}
                    const newRolId = modData?.new_rol_id ?? selected?.rol_id;
                    const rechazo  = modData?.rechazo_motivo;

                    return (
                        <InformacionUsuario
                            user={selected}
                            title="Detalle de modificación"
                            cols={2}
                            hideEmpty={false}
                            fields={[
                              { key: "nuevo_rol", label: "Nuevo rol", render: () => (newRolId ? getRolNombre(newRolId) : "—") },
                              ...(selected?.modificaciones_stage === "Rechazada"
                                  ? [{ key: "motivo", label: "Motivo de rechazo", render: () => rechazo || "—" }]
                                  : []),
                            ]}
                        />
                    );
                  })()
                  }

                  {/* 7) Responsable */}
                  <InformacionUsuario
                      user={selected}
                      title="Responsable"
                      cols={1}
                      fields={[ { key: "supervisor", label: "Responsable" } ]}
                  />
                </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDetailModal(false)}
            >
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
